import {
  WELL_COLS,
  WELL_ROWS,
  WELL_SIZE,
  idx,
  idxCol,
  idxRow,
  inBounds,
  type Cell,
  type CellIndex,
  type GemKind,
  type Grid,
  type Piece,
  type PieceCells,
} from './types';
import { composition, heaviestGem, pickWeightedGem, type GemWeights } from './gempool';
import { getMatchMin } from './rules';
import type { Rng } from './intent';

export function createGrid(): Grid {
  return new Array<Cell>(WELL_SIZE).fill(null);
}

/**
 * Seed a settled, match-free opening board: each column gets a random stack
 * height, filled from the floor up so nothing floats, with at least one empty
 * column so no row starts packed (a pre-made full row would hand the player a
 * free burst on their first card). No 3-run is ever placed.
 *
 * This exists so the well is a board to read on turn 1 rather than a blank pit.
 */
export function seedBoard(grid: Grid, rows: number, rng: Rng, weights: GemWeights): void {
  if (rows <= 0) return;
  if (composition(weights).length === 0) return;
  const maxH = Math.min(rows, WELL_ROWS);

  const heights: number[] = [];
  for (let c = 0; c < WELL_COLS; c++) heights.push(Math.floor(rng() * (maxH + 1)));
  if (!heights.includes(0)) heights[Math.floor(rng() * WELL_COLS)] = 0;

  for (let c = 0; c < WELL_COLS; c++) {
    const h = heights[c] ?? 0;
    for (let i = 0; i < h; i++) {
      const r = WELL_ROWS - 1 - i;
      const kind = pickSeedKind(grid, r, c, rng, weights);
      if (kind !== null) setCell(grid, r, c, kind);
    }
  }
}

/**
 * A weighted colour that would not complete a 3-run. Falls back to the
 * heaviest available colour rather than an arbitrary one, so a heavily cut
 * pool does not quietly lose its shape.
 */
function pickSeedKind(
  grid: Grid,
  r: number,
  c: number,
  rng: Rng,
  weights: GemWeights,
): GemKind | null {
  for (let tries = 0; tries < 16; tries++) {
    const kind = pickWeightedGem(rng, weights);
    if (kind === null || kind === 'stone') continue;
    if (completesRun(grid, r, c, kind)) continue;
    return kind;
  }
  return heaviestGem(weights);
}

/**
 * True if placing `kind` at (r, c) would complete a run of matchMin. Only
 * cells below and to the left are consulted — the fill order (left-to-right,
 * bottom-up) guarantees those are the only already-placed neighbours.
 */
function completesRun(grid: Grid, r: number, c: number, kind: GemKind): boolean {
  const matchMin = getMatchMin();

  let below = 0;
  while (at(grid, r + below + 1, c) === kind) below++;
  if (below >= matchMin - 1) return true;

  let left = 0;
  while (at(grid, r, c - left - 1) === kind) left++;
  if (left >= matchMin - 1) return true;

  return false;
}

export function cloneGrid(grid: Grid): Grid {
  return grid.slice();
}

export function at(grid: Grid, row: number, col: number): Cell {
  return inBounds(row, col) ? grid[idx(row, col)] ?? null : null;
}

export function setCell(grid: Grid, row: number, col: number, cell: Cell): void {
  if (!inBounds(row, col)) return;
  grid[idx(row, col)] = cell;
}

export function countGems(grid: Grid): number {
  let n = 0;
  for (const cell of grid) if (cell !== null) n++;
  return n;
}

export function countKind(grid: Grid, kind: GemKind): number {
  let n = 0;
  for (const cell of grid) if (cell === kind) n++;
  return n;
}

/** Orthogonally adjacent (no diagonals), by flat cell index. */
export function adjacentCells(a: CellIndex, b: CellIndex): boolean {
  return Math.abs(idxRow(a) - idxRow(b)) + Math.abs(idxCol(a) - idxCol(b)) === 1;
}

/** Bottom-up compaction of every column. Returns true if anything moved. */
export function applyGravity(grid: Grid): boolean {
  let moved = false;
  const stack: GemKind[] = [];
  for (let c = 0; c < WELL_COLS; c++) {
    stack.length = 0;
    for (let r = WELL_ROWS - 1; r >= 0; r--) {
      const cell = grid[idx(r, c)] ?? null;
      if (cell !== null) stack.push(cell);
    }
    for (let r = WELL_ROWS - 1, i = 0; r >= 0; r--, i++) {
      const next: Cell = i < stack.length ? stack[i] ?? null : null;
      const k = idx(r, c);
      if (grid[k] !== next) moved = true;
      grid[k] = next;
    }
  }
  return moved;
}

/** Deepest empty row in a column, or -1 if the column is packed. */
export function lowestEmptyRow(grid: Grid, col: number): number {
  for (let r = WELL_ROWS - 1; r >= 0; r--) {
    if (grid[idx(r, col)] === null) return r;
  }
  return -1;
}

export function isColumnFull(grid: Grid, col: number): boolean {
  return lowestEmptyRow(grid, col) === -1;
}

function pieceFits(grid: Grid, cells: PieceCells, anchorRow: number, anchorCol: number): boolean {
  for (const [dr, dc] of cells) {
    const r = anchorRow + dr;
    const c = anchorCol + dc;
    if (!inBounds(r, c)) return false;
    if (grid[idx(r, c)] !== null) return false;
  }
  return true;
}

/**
 * Lowest valid anchor row for `cells` dropped into `anchorCol`, or null if it
 * cannot be placed at all. The piece falls until the next row down would
 * collide — it does not teleport through gaps, which is what makes a
 * deliberately holed column a real hazard rather than a cosmetic one.
 */
export function restRow(grid: Grid, cells: PieceCells, anchorCol: number): number | null {
  let maxDr = 0;
  let minDc = 0;
  let maxDc = 0;
  for (const [dr, dc] of cells) {
    if (dr > maxDr) maxDr = dr;
    if (dc < minDc) minDc = dc;
    if (dc > maxDc) maxDc = dc;
  }
  if (anchorCol + minDc < 0 || anchorCol + maxDc >= WELL_COLS) return null;

  let lastValid: number | null = null;
  // Start with the whole piece above the board, then walk down.
  for (let r = -1 - maxDr; r < WELL_ROWS; r++) {
    if (pieceFits(grid, cells, r, anchorCol)) {
      lastValid = r;
    } else if (lastValid !== null) {
      break;
    }
  }
  return lastValid;
}

/**
 * Rotate 90° clockwise `quarterTurns` times, then normalize so the top-left
 * occupied cell sits at (0, 0). Normalizing makes the ghost preview's anchor
 * predictable; it is not SRS, and no wall-kick table exists. Fine for a
 * single-player drop where the player chooses the column freely.
 */
export function rotateCells(cells: PieceCells, quarterTurns: number): PieceCells {
  return rotatePiece({ cells, kinds: [] }, quarterTurns).cells;
}

/**
 * Rotate a piece, carrying its gem kinds along. Cells are sorted after
 * normalizing, so the kinds MUST travel with them — rotating a shape in
 * isolation and indexing a parallel kinds array would silently reassign colors
 * to different positions.
 */
export function rotatePiece(piece: Piece, quarterTurns: number): Piece {
  const turns = ((quarterTurns % 4) + 4) % 4;
  let pairs: [number, number, GemKind | null][] = piece.cells.map(([dr, dc], i) => [
    dr,
    dc,
    piece.kinds[i] ?? null,
  ]);

  for (let n = 0; n < turns; n++) {
    // 90° CW about the origin: (dr, dc) -> (dc, -dr)
    pairs = pairs.map(([dr, dc, k]) => [dc, -dr, k]);
  }

  let minDr = Infinity;
  let minDc = Infinity;
  for (const [dr, dc] of pairs) {
    if (dr < minDr) minDr = dr;
    if (dc < minDc) minDc = dc;
  }
  pairs = pairs.map(([dr, dc, k]) => [dr - minDr, dc - minDc, k]);

  // Sort so the cell order is stable under rotation.
  pairs.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  return {
    cells: pairs.map(([dr, dc]) => [dr, dc] as const),
    kinds: pairs.map(([, , k]) => k as GemKind),
  };
}

export function clearCells(grid: Grid, cells: Iterable<CellIndex>): void {
  for (const i of cells) grid[i] = null;
}

/** Snapshot the kinds at the given indices before they are cleared. */
export function harvestCells(grid: Grid, cells: Iterable<CellIndex>): Map<CellIndex, GemKind> {
  const out = new Map<CellIndex, GemKind>();
  for (const i of cells) {
    const cell = grid[i] ?? null;
    if (cell !== null) out.set(i, cell);
  }
  return out;
}

export function isCapped(grid: Grid, col: number): boolean {
  return grid[idx(0, col)] !== null;
}

export function cappedColumns(grid: Grid): number[] {
  const out: number[] = [];
  for (let c = 0; c < WELL_COLS; c++) if (isCapped(grid, c)) out.push(c);
  return out;
}

/** Rows 0..CAP_WIPE_ROWS-1 of a column are removed when it tops out. */
export const CAP_WIPE_ROWS = 5;

/**
 * End-Turn cap check: every column with an occupied top row is reported, and
 * its top CAP_WIPE_ROWS rows are wiped. Damage is the caller's job — this
 * module has no notion of HP, which is exactly why push-up below cannot
 * accidentally deal cap damage.
 */
export function applyCapCheck(grid: Grid): { columns: number[]; wiped: CellIndex[] } {
  const columns = cappedColumns(grid);
  const wiped: CellIndex[] = [];
  for (const c of columns) {
    for (let r = 0; r < CAP_WIPE_ROWS; r++) {
      const k = idx(r, c);
      if (grid[k] !== null) wiped.push(k);
      grid[k] = null;
    }
  }
  return { columns, wiped };
}

/** How many cells in each garbage row are left empty rather than filled with Stone. */
export const GARBAGE_HOLES = 2;

/**
 * Shift the whole board up by `rows` (destroying whatever is forced off the
 * top), then fill the now-empty bottom `rows` with Stone garbage rows.
 *
 * Each garbage row has GARBAGE_HOLES gaps chosen by the combat RNG — not a
 * solid wall. A solid Stone row already qualifies as a full row clear, so the
 * resolver would delete it on the player's next card for free. Holes turn it
 * into a spatial puzzle: the row reduces your vertical space, pushes you
 * toward the cap, and can only be cleared by filling the gaps (or Shatter).
 *
 * Deliberately does NOT cap-check. A push-up can leave the top row occupied;
 * that is the point — The Mason raises the floor and the bill arrives on your
 * next End Turn, not his. Returns the number of gems destroyed off the top.
 */
export function pushUpAndFillBottom(grid: Grid, rows: number, rng: Rng): number {
  if (rows <= 0) return 0;
  const shift = Math.min(rows, WELL_ROWS);
  let destroyed = 0;

  for (let c = 0; c < WELL_COLS; c++) {
    // Read the column out first so the shift cannot smear values.
    const col: Cell[] = new Array<Cell>(WELL_ROWS).fill(null);
    for (let r = 0; r < WELL_ROWS; r++) col[r] = grid[idx(r, c)] ?? null;

    for (let r = 0; r < WELL_ROWS; r++) {
      const from = r + shift;
      const next: Cell = from < WELL_ROWS ? col[from] ?? null : null;
      if (r < shift && col[r] !== null) destroyed++;
      grid[idx(r, c)] = next;
    }
  }

  const holeCount = Math.min(GARBAGE_HOLES, WELL_COLS);
  for (let r = WELL_ROWS - shift; r < WELL_ROWS; r++) {
    // Distinct holes per row, drawn from combat RNG so seeds reproduce them.
    const holes = new Set<number>();
    while (holes.size < holeCount) {
      holes.add(Math.floor(rng() * WELL_COLS));
    }
    for (let c = 0; c < WELL_COLS; c++) {
      grid[idx(r, c)] = holes.has(c) ? null : 'stone';
    }
  }
  return destroyed;
}

export function columnCells(grid: Grid, col: number): Cell[] {
  const out: Cell[] = [];
  for (let r = 0; r < WELL_ROWS; r++) out.push(grid[idx(r, col)] ?? null);
  return out;
}

export function rowCells(grid: Grid, row: number): Cell[] {
  const out: Cell[] = [];
  for (let c = 0; c < WELL_COLS; c++) out.push(grid[idx(row, c)] ?? null);
  return out;
}

/** Debug rendering for test failures. */
export function showGrid(grid: Grid): string {
  const symbol: Record<GemKind, string> = {
    ember: 'E', frost: 'F', venom: 'V', volt: 'L', stone: 'S',
  };
  const lines: string[] = [];
  for (let r = 0; r < WELL_ROWS; r++) {
    let line = '';
    for (let c = 0; c < WELL_COLS; c++) {
      const cell = grid[idx(r, c)] ?? null;
      line += cell === null ? '.' : symbol[cell];
    }
    lines.push(`${r} ${line}`);
  }
  return lines.join('\n');
}

export { idx, idxCol, idxRow };
